import { uniqueId } from 'lodash-es';
import QueryString from 'qs';
import React from 'react';
import { Box } from '@apia/theme';
import { SimpleButton } from '../../../components/common';
import { useFormContext } from '../../../components/common/validation/FormContext';
import { hasSucceedFormValidation } from '../../../store/validation/util';
import validationsStore from '../../../store/validation/validationStore';
import { TApiaFormButton, TApiaLoad, TApiaLoadForm } from '../../../types';
import { arrayOrArray } from '../../../utils/util';
import ApiaApi from '../apiaApi';
import { getFunction, TApiaApiMethodHandler } from '../ApiaApiHandler';
import { ISimpleButton } from '../../../components/common/buttons/SimpleButton';

type TApiaApiButtonsContainer = TApiaApiMethodHandler & {
  definition?: TApiaLoad<TApiaLoadForm> | undefined;
};

type TApiaApiButton = Omit<TApiaFormButton, 'type'> &
  Pick<ISimpleButton, 'type'> & {
    buttonKey: string;
  };

const parseButtons = (buttons: TApiaFormButton[]): TApiaApiButton[] => {
  return buttons.map((currentButton) => ({
    ...currentButton,
    buttonKey: uniqueId(),
    type: ['submitAjax', 'submit'].includes(currentButton.type)
      ? 'submit'
      : 'button',
  }));
};

const ApiaApiButtonsContainer = (props: TApiaApiButtonsContainer) => {
  const buttonElements = React.useMemo(
    () => arrayOrArray(props.definition?.form.buttons?.button ?? []),
    [props.definition?.form.buttons?.button],
  );
  const buttons = React.useMemo(
    () => parseButtons(buttonElements),
    [buttonElements],
  );
  const modalConfiguration = React.useMemo(
    () => props.configuration?.modalConfiguration,
    [props.configuration?.modalConfiguration],
  );
  const methodsPath = React.useMemo(
    () => props.configuration?.methodsPath,
    [props.configuration?.methodsPath],
  );
  const { name: apiaApiForm } = useFormContext();

  const renderButton = React.useCallback(
    (currentButton: TApiaApiButton) => {
      const key = currentButton.buttonKey;
      const className = `handler__${currentButton.type ?? ''}`;
      const onClick = () => {
        void (async function submitForm() {
          const validationResult =
            await validationsStore.validateForm(apiaApiForm);
          if (!hasSucceedFormValidation(validationResult)) {
            return;
          }
          const { submitValues } = validationResult;

          if (props?.definition && currentButton.type === 'submit') {
            const formData = new FormData();
            Object.entries(submitValues).forEach(([name, value]) => {
              formData.append(name, (value as string | Blob) ?? '');
            });

            const hasContext = props?.definition.form.action.match(
              new RegExp(`^${window.CONTEXT}/`),
            );
            const url = `${hasContext ? '' : window.CONTEXT}${
              !props?.definition.form.action.startsWith('/') ? '/' : ''
            }${props?.definition.form.action}`;
            console.log(url);

            props.setState((current) => ({
              ...current,
              isLoading: true,
            }));

            const response = await ApiaApi.post(url, {
              postData: props.state.isMultipart
                ? formData
                : QueryString.stringify(
                    [...formData.entries(), ['isAjax', true]].reduce<
                      Record<string, string>
                    >((accumulated, [name, value]) => {
                      const retValue = { ...accumulated };

                      retValue[name.toString()] = String(value);
                      return retValue;
                    }, {}),
                  ),
              handleLoad: true,
              notificationsCategory: 'apiaApiHandler',
              modalConfiguration,
              methodsPath,
            });
            if (modalConfiguration?.onSubmitted) {
              if (
                (await modalConfiguration.onSubmitted(
                  { ...props },
                  response,
                )) === false
              )
                return;
            }
          }

          if (currentButton.onclick) {
            const actions = currentButton.onclick.split(';');
            Object.values(actions).map(async (action) => {
              const method = await getFunction(action, {
                ...props,
              });

              if (method)
                method({
                  currentUrl: props.definition?.form.action ?? 'noUrl',
                });
              else {
                throw new Error(
                  `The requested action is not defined: "${
                    props.definition?.form.action ?? ''
                  }"`,
                );
              }
            });
          }
        })();
      };
      return (
        <SimpleButton
          className={className}
          disabled={props.state.disabled}
          id={currentButton.id || currentButton.text}
          isLoading={props.state.isLoading}
          key={key}
          title={currentButton.text}
          type={currentButton.type}
          onClick={onClick}
        >
          {currentButton.text}
        </SimpleButton>
      );
    },
    [apiaApiForm, methodsPath, modalConfiguration, props],
  );

  return (
    buttons.length > 0 && (
      <Box className="handler__form__buttons">
        {buttons.map((currentButton) => renderButton(currentButton))}
      </Box>
    )
  );
};

export default React.memo(ApiaApiButtonsContainer);
